import React, { useEffect, useState } from 'react';
import { api, useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Loader2, Network, Users, AlertTriangle, Sparkles, CalendarDays, ArrowRight, Star } from 'lucide-react';
import SetCaseloadDialog from './caseloads/SetCaseloadDialog';
import TeamsTab from './caseloads/TeamsTab';
import PlannerBoard from './caseloads/PlannerBoard';

const LOOKBACKS = [[30, '1 mo'], [60, '2 mo'], [90, '3 mo'], [180, '6 mo'], [365, '12 mo']];
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const contBar = (pct) => pct >= 80 ? 'bg-emerald-500' : pct >= 60 ? 'bg-amber-500' : 'bg-red-500';

const KPI = ({ label, value, testid }) => (
  <div className="rounded-xl border border-[#DED8EB] bg-white px-4 py-3" data-testid={testid}>
    <div className="text-2xl font-extrabold text-[#2D1B4E]" style={{ fontFamily: 'Manrope, sans-serif' }}>{value}</div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#8E85A0]">{label}</div>
  </div>
);

export default function CaseloadsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('planner');
  const [evaluating, setEvaluating] = useState(false);
  const [evalText, setEvalText] = useState(null);
  const [lookback, setLookback] = useState(180);
  const [scenario, setScenario] = useState('');
  const [setFor, setSetFor] = useState(null);
  const [plannerStaged, setPlannerStaged] = useState(0);

  const guard = (fn) => {
    if (tab === 'planner' && plannerStaged > 0 &&
        !window.confirm(`You have ${plannerStaged} unapplied planner change${plannerStaged > 1 ? 's' : ''} — leaving will discard them. Continue?`)) return;
    setPlannerStaged(0);
    fn();
  };

  const allowed = user && ['administrator', 'supervisor'].includes(user.role);

  const load = async () => {
    try {
      const { data: d } = await api.get('/caseloads/overview', { params: { lookback_days: lookback } });
      setData(d);
      if (d.last_eval && !evalText) setEvalText(d.last_eval);
    } catch (e) { toast.error('Failed to load caseloads'); }
    setLoading(false);
  };

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed, lookback]);

  const runEvaluation = async () => {
    setEvaluating(true);
    try {
      const { data: d } = await api.post('/caseloads/evaluate', { scenario, lookback_days: lookback });
      setEvalText(d);
      toast.success(scenario ? 'Scenario modeled' : 'AI evaluation complete');
    } catch (e) { toast.error(e.response?.data?.detail || 'Evaluation failed'); }
    setEvaluating(false);
  };

  if (!allowed) {
    return <div className="p-10 text-center text-[#8E85A0]" data-testid="caseloads-denied">Caseloads is restricted to administrators and supervisors.</div>;
  }
  if (loading) return <div className="py-24 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#6B3FA0]" /></div>;
  if (!data) return null;

  const { kpis, participants, staff, crossover, streamline } = data;
  const flagged = participants.filter(p => p.flags.length > 0);

  return (
    <div className={`p-4 sm:p-6 lg:p-8 space-y-5 ${tab === 'planner' ? '' : 'max-w-7xl'}`} data-testid="caseloads-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Network className="h-8 w-8 text-[#6B3FA0]" /> Caseloads
          </h1>
          <p className="text-base text-[#5A4D6E] mt-1">Official caseloads, crossover, and continuity</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-full border border-[#DED8EB] overflow-hidden" data-testid="lookback-selector">
            {LOOKBACKS.map(([v, l]) => (
              <button key={v} onClick={() => guard(() => setLookback(v))}
                      className={`px-3 py-1.5 text-xs font-bold ${lookback === v ? 'bg-[#6B3FA0] text-white' : 'bg-white text-[#5A4D6E] hover:bg-[#F0EDF6]'}`}
                      data-testid={`lookback-${v}`}>{l}</button>
            ))}
          </div>
        </div>
      </div>

      {tab !== 'planner' && (<>
      <div className="rounded-xl border border-[#DED8EB] bg-white p-3 space-y-2" data-testid="ai-scenario-box">
        <label className="text-xs font-bold uppercase tracking-wider text-[#6B3FA0] flex items-center gap-1.5">
          <Sparkles className="h-3.5 w-3.5" /> Ask the AI to model a scenario
        </label>
        <textarea value={scenario} onChange={e => setScenario(e.target.value)} rows={2}
                  placeholder={'e.g. "What if Sarah leaves next month — who covers her caseload with alternates?" · "Consolidate Explore Mondays under 3 staff" · leave blank for a full efficiency evaluation'}
                  className="w-full rounded-lg border border-[#DED8EB] px-3 py-2 text-sm text-[#2D1B4E] placeholder:text-[#B9B2C9] focus:outline-none focus:border-[#9B7ED8]"
                  data-testid="scenario-input" />
        <Button onClick={runEvaluation} disabled={evaluating} className="bg-[#2D1B4E] hover:bg-[#3D2A63] text-white gap-1.5" data-testid="caseloads-evaluate-btn">
          {evaluating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          {evaluating ? 'Thinking…' : scenario.trim() ? 'Model this scenario' : 'AI: Evaluate current model'}
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="caseloads-kpis">
        <KPI label="Participants tracked" value={kpis.participants} testid="kpi-participants" />
        <KPI label="Active staff" value={kpis.staff_active} testid="kpi-staff" />
        <KPI label="Avg continuity" value={`${kpis.avg_continuity}%`} testid="kpi-continuity" />
        <KPI label="Avg staff / participant" value={kpis.avg_staff_per_participant} testid="kpi-avg-staff" />
        <KPI label="Flagged" value={kpis.flagged} testid="kpi-flagged" />
      </div>

      {evalText && (
        <div className="rounded-xl border border-[#D9CBA8] bg-[#FDFBF4] p-4" data-testid="caseloads-eval">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-[#8A6D1F] flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5" /> {evalText.scenario ? `AI scenario: "${evalText.scenario}"` : 'AI efficiency evaluation'}
            </span>
            <span className="text-[10px] text-[#8E85A0]">{new Date(evalText.created_at).toLocaleString()} · {evalText.created_by}</span>
          </div>
          <div className="text-sm text-[#3A3045] whitespace-pre-wrap leading-relaxed" data-testid="caseloads-eval-text">
            {evalText.text.replace(/^### /gm, '• ').replace(/^## /gm, '— ').replace(/\*\*/g, '')}
          </div>
        </div>
      )}
      </>)}

      <div className="flex gap-1.5">
        {[['planner', 'Planner'], ['participants', `Participants (${participants.length})`], ['staff', `Staff caseloads (${staff.length})`], ['teams', 'Teams'], ['streamline', `Streamline (${(streamline || []).length})`], ['crossover', `Crossover (${crossover.length})`]].map(([k, label]) => (
          <button key={k} onClick={() => guard(() => setTab(k))}
            className={`px-3.5 py-2 rounded-full text-xs font-bold border ${tab === k ? 'bg-[#2D1B4E] text-white border-[#2D1B4E]' : 'text-[#5A4D6E] border-[#DED8EB] hover:border-[#9B7ED8]'}`}
            data-testid={`caseloads-tab-${k}`}>{label}</button>
        ))}
      </div>

      {tab === 'participants' && (
        <div className="space-y-2" data-testid="caseloads-participants">
          {flagged.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5" /> {flagged.length} participant{flagged.length > 1 ? 's' : ''} flagged for continuity risk — shown first
            </div>
          )}
          {participants.map(p => (
            <div key={p.name} className="rounded-xl border border-[#DED8EB] bg-white p-3.5" data-testid={`caseload-participant-${p.name}`}>
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-bold text-[#2D1B4E]">{p.name}</span>
                {p.service_line && <span className="text-[10px] font-bold uppercase text-[#6B3FA0] bg-[#F0EAFA] rounded-full px-2 py-0.5">{data.line_labels[p.service_line] || p.service_line}</span>}
                {!p.active && <span className="text-[10px] font-bold uppercase text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">inactive</span>}
                <span className="text-xs text-[#8E85A0]">{p.total_shifts} shifts · {p.distinct_staff} staff · primary: <b className="text-[#5A4D6E]">{p.primary}</b></span>
                {p.attend_days?.length > 0 && (
                  <span className="text-[10px] font-bold uppercase text-[#2D6A4F] bg-[#E8F5EE] border border-[#BFE3CF] rounded-full px-2 py-0.5 flex items-center gap-1">
                    <CalendarDays className="h-3 w-3" /> here {p.attend_days.map(d => d[0].toUpperCase() + d.slice(1, 3)).join(' · ')}
                  </span>
                )}
                <div className="ml-auto flex items-center gap-2 min-w-[140px]">
                  <div className="flex-1 h-2 rounded-full bg-[#EEE9F6] overflow-hidden w-24">
                    <div className={`h-full ${contBar(p.continuity)}`} style={{ width: `${p.continuity}%` }} />
                  </div>
                  <span className="text-xs font-bold text-[#2D1B4E] w-10 text-right">{p.continuity}%</span>
                </div>
              </div>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {p.staff.map(s => (
                  <span key={s.id} className="text-[11px] font-semibold text-[#5A4D6E] bg-[#F7F4FB] border border-[#E8E2F2] rounded-full px-2 py-0.5">
                    {s.name} <b className="text-[#6B3FA0]">{s.count}×</b> ({s.pct}%)
                  </span>
                ))}
              </div>
              {p.flags.length > 0 && (
                <div className="mt-1.5 text-[11px] font-semibold text-amber-700 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {p.flags.join(' · ')}
                </div>
              )}
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                {p.assigned ? (
                  <>
                    <span className="text-[11px] font-bold text-white bg-[#6B3FA0] rounded-full px-2 py-0.5 flex items-center gap-1" data-testid={`assigned-primary-${p.name}`}>
                      <Star className="h-3 w-3" /> {p.assigned.primary_name}
                    </span>
                    {p.assigned.alternates.map(a => (
                      <span key={a.id} className="text-[11px] font-semibold text-[#6B3FA0] bg-[#F0EAFA] border border-[#D9C9F2] rounded-full px-2 py-0.5">alt: {a.name}</span>
                    ))}
                  </>
                ) : (
                  <span className="text-[11px] text-[#B9B2C9] italic">no official caseload set</span>
                )}
                <button onClick={() => setSetFor(p)}
                        className="text-[11px] font-bold text-[#6B3FA0] border border-[#D9C9F2] rounded-full px-2.5 py-0.5 hover:bg-[#F7F2FD]"
                        data-testid={`set-caseload-${p.name}`}>
                  {p.assigned ? 'Edit caseload' : 'Set caseload'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'staff' && (
        <div className="grid sm:grid-cols-2 gap-2" data-testid="caseloads-staff">
          {staff.map(s => (
            <div key={s.id} className="rounded-xl border border-[#DED8EB] bg-white p-3.5" data-testid={`caseload-staff-${s.id}`}>
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-[#6B3FA0]" />
                <span className="font-bold text-[#2D1B4E]">{s.name}</span>
                <span className="text-xs text-[#8E85A0]">{s.participant_count} participant{s.participant_count !== 1 ? 's' : ''} · {s.total_shifts} shifts{s.avg_weekly_hours ? ` · avg ${s.avg_weekly_hours}h/wk` : ''}</span>
                {s.work_days?.length > 0 && (
                  <span className="text-[10px] font-bold uppercase text-[#1D4E89] bg-[#EAF2FB] border border-[#C9DDF2] rounded-full px-2 py-0.5">
                    works {s.work_days.map(d => d[0].toUpperCase() + d.slice(1, 3)).join(' · ')}
                  </span>
                )}
                {s.participant_count >= 6 && <span className="ml-auto text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">spread thin</span>}
              </div>
              <div className="mt-2 flex gap-1.5 flex-wrap">
                {s.caseload.map(c => (
                  <span key={c.participant} className="text-[11px] font-semibold text-[#5A4D6E] bg-[#F7F4FB] border border-[#E8E2F2] rounded-full px-2 py-0.5">
                    {c.participant} <b className="text-[#6B3FA0]">{c.count}×</b>
                  </span>
                ))}
              </div>
              <div className="mt-2 flex items-center gap-1 flex-wrap">
                <span className="text-[10px] font-bold uppercase text-[#8E85A0] mr-1">Availability:</span>
                {DAY_KEYS.map(d => {
                  const on = (s.availability || {})[d] !== false;
                  return (
                    <button key={d}
                      onClick={async () => {
                        const next = { ...(s.availability || {}), [d]: !on };
                        try {
                          await api.put(`/caseloads/staff-availability/${s.id}`, { availability: Object.fromEntries(DAY_KEYS.map(k => [k, k === d ? !on : (s.availability || {})[k] !== false])) });
                          setData(prev => ({ ...prev, staff: prev.staff.map(x => x.id === s.id ? { ...x, availability: next } : x) }));
                          toast.success(`${s.name}: ${d.toUpperCase()} ${!on ? 'available' : 'unavailable'}`);
                        } catch (e) { toast.error('Failed to update availability'); }
                      }}
                      className={`h-6 w-9 rounded-md text-[10px] font-black uppercase border transition-colors ${on ? 'bg-[#E8F5EE] text-[#2D6A4F] border-[#BFE3CF]' : 'bg-gray-100 text-gray-400 border-gray-200 line-through'}`}
                      title={`Toggle ${d}`} data-testid={`avail-${s.id}-${d}`}>
                      {d.slice(0, 2)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'planner' && <PlannerBoard lookback={lookback} onStagedChange={setPlannerStaged} />}

      {tab === 'teams' && <TeamsTab lookback={lookback} lineLabels={data.line_labels} />}

      {tab === 'streamline' && (
        <div className="space-y-2" data-testid="caseloads-streamline">
          <p className="text-xs text-[#8E85A0]">Day-aligned consolidation picks: for each fragmented caseload, the staff member whose working days best match when the participant is actually here — weighted by familiarity and open capacity.</p>
          {(streamline || []).length === 0 ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-6 text-center text-sm font-semibold text-emerald-800" data-testid="streamline-empty">
              Nothing to streamline — every participant with regular attendance already has a tight, consistent staff team. 🎉
            </div>
          ) : (streamline || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-[#DED8EB] bg-white p-3.5" data-testid={`streamline-${i}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-bold text-[#2D1B4E]">{s.participant}</span>
                <span className="text-[10px] font-bold uppercase text-[#2D6A4F] bg-[#E8F5EE] border border-[#BFE3CF] rounded-full px-2 py-0.5">here {s.attend_days.join(' · ')}</span>
                <span className="text-xs text-[#8E85A0]">{s.current_staff_count} staff · {s.continuity}% continuity</span>
                <ArrowRight className="h-4 w-4 text-[#6B3FA0]" />
                <span className="font-bold text-[#6B3FA0]">{s.suggested_primary}</span>
                <span className="text-[10px] font-bold uppercase text-white bg-[#6B3FA0] rounded-full px-2 py-0.5">suggested primary</span>
              </div>
              <p className="text-xs text-[#5A4D6E] mt-1.5">{s.reason}</p>
              {s.alternatives?.length > 0 && (
                <p className="text-[11px] text-[#8E85A0] mt-1">
                  Backups: {s.alternatives.map(a => `${a.name} (${a.cover}% day match${a.known ? `, knows them ${a.known}×` : ''})`).join(' · ')}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'crossover' && (
        <div className="space-y-2" data-testid="caseloads-crossover">
          <p className="text-xs text-[#8E85A0]">Staff pairs who share participants — natural swap/cover partners. High overlap also signals duplicated coverage.</p>
          {crossover.map((c, i) => (
            <div key={i} className="rounded-xl border border-[#DED8EB] bg-white p-3.5 flex items-center gap-3 flex-wrap" data-testid={`crossover-${i}`}>
              <span className="font-bold text-[#2D1B4E]">{c.a_name} <span className="text-[#9B7ED8]">×</span> {c.b_name}</span>
              <span className="text-xs font-bold text-[#6B3FA0] bg-[#F0EAFA] rounded-full px-2 py-0.5">{c.count} shared</span>
              <span className="text-xs text-[#8E85A0]">{c.shared.join(', ')}</span>
            </div>
          ))}
          {crossover.length === 0 && <p className="text-sm text-[#8E85A0] py-6 text-center">No staff currently share participants.</p>}
        </div>
      )}
      {setFor && (
        <SetCaseloadDialog participant={setFor.name} lookback={lookback} current={setFor.assigned}
                           onClose={() => setSetFor(null)} onSaved={load} />
      )}
    </div>
  );
}
